<template>
  <div class="signup-wizard-root signup-atmosphere">
    <CloudLayer />

    <!-- Persistent topbar (brand · progress · save&exit). Stays mounted
         across step transitions so the screen never blanks while a step
         fades out. -->
    <header class="signup-step__topbar signup-shell-content">
      <div class="signup-step__brand">
        <BrandMark />
      </div>
      <div class="signup-step__progress">
        <SignupProgressIndicator
          :current="currentStep"
          :total="totalSteps"
          :step-name="currentStepName"
        />
      </div>
      <!-- Right column intentionally empty: keeps the 3-column grid so the
           progress indicator stays centered against the viewport. The old
           Save & exit button has been removed — onboarding is forward-only,
           and the underlying state already auto-persists to sessionStorage
           if the user closes the tab. -->
      <div class="signup-step__exit-spacer" aria-hidden="true"></div>
    </header>

    <main class="signup-step__main signup-shell-content">
      <transition name="step-slide" mode="out-in">
        <component
          :is="currentStepComponent"
          :key="currentStep"
          @next="onNext"
          @back="onBack"
          @goto-step="onGotoStep"
        />
      </transition>
    </main>
  </div>
</template>

<script>
import WizardCompanyStep from './WizardCompanyStep.vue'
import WizardCoreValuesStep from './WizardCoreValuesStep.vue'
import WizardTeamsStep from './WizardTeamsStep.vue'
import WizardRewardsStep from './WizardRewardsStep.vue'
import WizardReviewStep from './WizardReviewStep.vue'
import CloudLayer from './CloudLayer.vue'
import BrandMark from './BrandMark.vue'
import SignupProgressIndicator from './SignupProgressIndicator.vue'
import {
  TOTAL_STEPS,
  FIRST_STEP_SLUG,
  STEP_NAMES,
  isStepSlug,
  indexFromSlug,
  wizardPathForStep,
} from './_step-slugs'

// Index-keyed for the <component :is> lookup. The order MUST match
// STEP_SLUGS in `_step-slugs.ts`.
const STEP_COMPONENTS = {
  1: 'WizardCompanyStep',
  2: 'WizardCoreValuesStep',
  3: 'WizardTeamsStep',
  4: 'WizardRewardsStep',
  5: 'WizardReviewStep',
}

export default {
  name: 'SignupWizard',
  components: {
    WizardCompanyStep,
    WizardCoreValuesStep,
    WizardTeamsStep,
    WizardRewardsStep,
    WizardReviewStep,
    CloudLayer,
    BrandMark,
    SignupProgressIndicator,
  },
  data() {
    return { totalSteps: TOTAL_STEPS }
  },
  computed: {
    pinVerified() {
      return !!(this.$store.state.signup && this.$store.state.signup.pinVerified)
    },
    currentSlug() {
      const slug = this.$route.params.slug
      return isStepSlug(slug) ? slug : FIRST_STEP_SLUG
    },
    currentStep() {
      return indexFromSlug(this.currentSlug)
    },
    currentStepName() {
      return STEP_NAMES[this.currentSlug] || ''
    },
    currentStepComponent() {
      return STEP_COMPONENTS[this.currentStep] || STEP_COMPONENTS[1]
    },
  },
  watch: {
    '$route.params.slug'(next) {
      if (!isStepSlug(next)) {
        this.$router.replace(wizardPathForStep(FIRST_STEP_SLUG)).catch(() => {})
      }
    },
    /**
     * On every step change, briefly accelerate the cloud-layer drift via
     * the global `body.is-rising` class. CloudLayer.vue owns the CSS that
     * reads this class. Skipped entirely under prefers-reduced-motion.
     */
    currentStep() {
      this.triggerRising()
    },
  },
  created() {
    if (!this.pinVerified) {
      this.$router.replace('/signup')
      return
    }
    if (!isStepSlug(this.$route.params.slug)) {
      this.$router.replace(wizardPathForStep(FIRST_STEP_SLUG)).catch(() => {})
    }
  },
  beforeDestroy() {
    if (this._risingTimeout) {
      clearTimeout(this._risingTimeout)
      this._risingTimeout = null
    }
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('is-rising')
    }
  },
  methods: {
    onBack() {
      const cur = this.currentStep
      if (cur <= 1) {
        this.$router.push('/signup/verify')
        return
      }
      this.$router.push(wizardPathForStep(cur - 1))
    },
    async onNext() {
      const cur = this.currentStep
      if (cur < TOTAL_STEPS) {
        this.$router.push(wizardPathForStep(cur + 1))
        return
      }
      try {
        await this.$store.dispatch('signup/submitSignup')
      } catch (e) {
        // submitError is already committed by the action; surfaced by step 6.
      }
    },
    onGotoStep(targetStep) {
      // Accepts either a 1-based step index (legacy LaunchEditModal contract)
      // or a slug. Normalizes through wizardPathForStep.
      if (typeof targetStep === 'number') {
        if (!Number.isFinite(targetStep) || targetStep < 1 || targetStep > TOTAL_STEPS) return
        this.$router.push(wizardPathForStep(targetStep))
        return
      }
      if (isStepSlug(targetStep)) {
        this.$router.push(wizardPathForStep(targetStep))
      }
    },
    triggerRising() {
      if (typeof window === 'undefined' || typeof document === 'undefined') return
      try {
        const mql = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)')
        if (mql && mql.matches) return
      } catch (e) {
        // matchMedia unavailable — proceed with the toggle.
      }
      if (!document.body) return
      if (this._risingTimeout) {
        clearTimeout(this._risingTimeout)
        this._risingTimeout = null
      }
      document.body.classList.add('is-rising')
      this._risingTimeout = window.setTimeout(() => {
        if (document.body) document.body.classList.remove('is-rising')
        this._risingTimeout = null
      }, 1400)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.signup-wizard-root {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Persistent topbar — three-column grid keeps the progress indicator
   centered regardless of how wide the brand or exit clusters get.
   Tighter vertical padding (20px instead of 24px) lets the editorial
   step name underneath the pills breathe without inflating the chrome. */
.signup-step__topbar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 20px 32px;
  gap: 16px;

  @media (max-width: 640px) {
    padding: 14px 20px;
    grid-template-columns: auto 1fr auto;
  }
}

.signup-step__brand {
  display: flex;
  align-items: center;
  justify-self: start;
}
.signup-step__progress {
  justify-self: center;
  display: flex;
  align-items: center;
}
/* Empty right column — preserves the 3-column grid so the centered
   progress indicator stays visually centered against the viewport. */
.signup-step__exit-spacer { justify-self: end; }

/* Phase G — main is now a flex column so the card can cap its height
   at the available viewport space. With `min-height: 0`, the card's
   internal `overflow-y: auto` body actually scrolls instead of pushing
   the whole page tall. */
.signup-step__main {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 24px 20px;

  @media (max-width: 640px) { padding: 12px 16px 16px; }
}

/* Step transition — only the card content fades + slides; the
   atmosphere/clouds/topbar stay mounted, so the screen never blanks.
   Slight overlap (the new card starts entering 60ms before the old
   leaves) keeps the gap from showing as background while the transform
   eases in. */
.step-slide-enter-active,
.step-slide-leave-active {
  transition: opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.step-slide-enter { opacity: 0; transform: translateX(12px); }
.step-slide-leave-to { opacity: 0; transform: translateX(-12px); }
</style>
